import React, { useCallback, useEffect, useMemo } from 'react';
import { getToken, removeTokens, setToken } from '../lib/token-storage';
import { setAccessToken } from '../lib/auth-store';
import { getOrCreateDeviceId } from '../lib/deviceId';
import { cancelProactiveRefresh, scheduleProactiveRefresh } from '../lib/auth-refresh-scheduler';
import { subscribeAuthExpired } from '../lib/auth-events';
import { useAppResume } from '../hooks/useAppLifecycle';
import { refreshAccessToken } from '../lib/refresh-token';
import { getTokenExpiryMs, getTokenSub } from '../lib/jwt-utils';
import {
  claimAndSwitchToUser,
  initStorageScopeOnBoot,
  switchToAnonymous,
} from '../lib/itinerary-scope';
import { identify, reset, track } from '../lib/analytics';

/** When the app/tab returns to foreground, refresh proactively if the
 *  access token is within this window of expiry (or already expired).
 *  Soft-keyboard- and timer-throttling on iOS Safari means a backgrounded
 *  setTimeout may have missed its proactive window — covering it here
 *  prevents a 401 on the user's first interaction post-resume. */
const RESUME_REFRESH_LEAD_MS = 60_000;

const AUTH_KEYS = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
};

export interface UserInfo {
  id: string;
  email: string;
  subscriptionTier: string;
  displayName: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
  /** Mirrors Stripe invoice state. `'past_due'` when a renewal
   *  charge fails — UI can show a dunning banner. `null` (or
   *  `'active'`) means nothing to surface. The JWT doesn't carry
   *  this; it's populated from `auth.me`, so the value lags a
   *  refresh behind a webhook delivery. */
  paymentStatus?: 'active' | 'past_due' | null | undefined;
  /** Admin access flag (ADR-0085). Populated from `auth.me`.
   *  Grants access to `/admin/revenue` and other internal tooling.
   *  Defaults to false when absent. */
  isAdmin?: boolean | undefined;
}

type AuthContextValue = {
  isAuthenticated: boolean;
  loading: boolean;
  user: UserInfo | null;
  /** True if token storage held an access or refresh token at cold-start —
   *  snapshot BEFORE init's refresh/clear logic so it survives a failed
   *  refresh. Lets `SplashGate` distinguish "expired session, send to
   *  /login" from "never signed in, send to marketing /". */
  hadPriorSession: boolean;
  setAuthenticated: (v: boolean) => void;
  setAuthFromTokens: (access: string | null, refresh?: string | null | undefined) => void;
  /** Persist tokens to storage and update auth state. Use after login/signup/passkey. */
  persistAndSetAuth: (accessToken: string, refreshToken?: string | null | undefined) => Promise<void>;
  /** Replace the cached `user` without rotating tokens. Use after a
   *  server-side change that updates user fields the JWT can't see
   *  yet — e.g. Stripe webhook bumped the subscription tier, but the
   *  client's JWT still carries the old `tier` claim. Server-side
   *  procedures already read the live tier from DB (see
   *  `auth-context.ts`), so this is purely a UI-state refresh. */
  setUserInfo: (info: UserInfo | null) => void;
  logout: () => Promise<void>;
};

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [user, setUser] = React.useState<UserInfo | null>(null);
  const [hadPriorSession, setHadPriorSession] = React.useState(false);

  const setAuthFromTokens = useCallback(
    (access: string | null, refresh?: string | null | undefined) => {
      setAccessToken(access);
      setIsAuthenticated(!!access);
      // Persist the refresh token whenever a caller supplies one. Mostly
      // a no-op safety net — `persistAndSetAuth` already writes it on
      // login, and `refreshAccessToken` writes the rotated token directly
      // — but lets a future callsite update both atomically through the
      // same surface. Fire-and-forget; storage success is not a precondition
      // for the in-memory state update.
      if (refresh) void setToken(AUTH_KEYS.refreshToken, refresh);
      if (access) {
        try {
          const parts = access.split('.');
          if (parts.length >= 2) {
            const payload = JSON.parse(
              atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'))
            ) as Record<string, unknown>;
            setUser({
              id: String(payload.sub ?? ''),
              email: String(payload.email ?? ''),
              subscriptionTier: String(payload.tier ?? 'basic'),
              displayName: typeof payload.displayName === 'string' ? payload.displayName : null,
              avatarUrl: typeof payload.avatarUrl === 'string' ? payload.avatarUrl : null,
              emailVerified: Boolean(payload.emailVerified ?? false),
            });
          }
        } catch {
          setUser(null);
        }
        // Arm a proactive refresh ~60s before this token expires. The
        // scheduler is a no-op for tokens with no `exp` claim.
        scheduleProactiveRefresh(access);
      } else {
        setUser(null);
        cancelProactiveRefresh();
      }
    },
    []
  );

  /** Shared teardown for explicit logout AND auth-expired events. */
  const clearAuth = useCallback(async () => {
    cancelProactiveRefresh();
    await removeTokens([AUTH_KEYS.accessToken, AUTH_KEYS.refreshToken]);
    setAccessToken(null);
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const persistAndSetAuth = useCallback(
    async (accessToken: string, refreshToken?: string | null | undefined) => {
      // Capture the transition. If we were anonymous a moment ago, the
      // itinerary store is still pointed at the anonymous persist scope;
      // we hand it off to the user scope (and claim any pre-login trips)
      // below.
      const wasAnonymous = !isAuthenticated;
      await setToken(AUTH_KEYS.accessToken, accessToken);
      if (refreshToken) await setToken(AUTH_KEYS.refreshToken, refreshToken);
      // CRITICAL: switch the itinerary scope BEFORE `setAuthFromTokens`.
      // SyncManagerInit's `[isAuthenticated]` effect kicks off
      // `hydrateFromServer` the moment `isAuthenticated` flips true; if
      // we were still in the anonymous scope when that fires, the
      // downloaded server trips would persist into the anonymous slot
      // (visible to the next visitor on a shared device) before the
      // claim flow ever ran. Doing the scope switch first means the
      // hydrate lands in the user's own slot.
      if (wasAnonymous) {
        const userId = getTokenSub(accessToken);
        if (userId) {
          await claimAndSwitchToUser(userId);
        }
      }
      setAuthFromTokens(accessToken, refreshToken);
    },
    [setAuthFromTokens, isAuthenticated]
  );

  const logout = useCallback(async () => {
    // Fire BEFORE clearAuth so the event still carries the user's distinct
    // id; the `[user]` effect below resets the SDK identity once user
    // flips to null.
    track('auth.signed_out');
    // ORDER MATTERS: switchToAnonymous before clearAuth.
    // switchToAnonymous flushes pending writes (SyncManager + UserPrefsSync)
    // before flipping the storage scope; both flushes need the access
    // token to still be in memory or the API rejects them with 401.
    // clearAuth then wipes the in-memory + persisted tokens. Reversing the
    // order would force the flushes to 401 (or hit our auth-gate no-op),
    // dropping a pref edit caught inside the 1.5s UserPrefsSync debounce.
    await switchToAnonymous();
    await clearAuth();
  }, [clearAuth]);

  // Listen for terminal-401 / refresh-failure events from the tRPC fetch
  // wrapper or the proactive scheduler. Wipes in-memory + persisted auth so
  // the app redirects to sign-in. We don't flush here — this event already
  // implies the API rejected our token, so a flush would just fail again.
  // Also flips the itinerary persist scope back to anonymous so the
  // user's local trips aren't left visible to the next person who lands
  // on this device.
  useEffect(() => {
    return subscribeAuthExpired(async () => {
      console.warn('[auth] session expired — clearing tokens');
      await clearAuth();
      await switchToAnonymous();
    });
  }, [clearAuth]);

  // Mirror the authenticated user into the analytics identity. Re-fires on
  // every meaningful change (sign-in, tier bump, sign-out) so PostHog stays
  // in sync without each callsite having to remember to call `identify`.
  useEffect(() => {
    if (user) {
      identify(user.id, {
        plan: user.subscriptionTier,
        email: user.email,
        emailVerified: user.emailVerified,
      });
    } else {
      reset();
    }
  }, [user]);

  // When the app/tab returns to foreground after being inactive, the
  // proactive `setTimeout` may have been throttled or paused (iOS Safari
  // is the worst offender). Refresh up-front if the access token is at or
  // near expiry so the user's next interaction starts with a fresh token.
  useAppResume(() => {
    if (!isAuthenticated) return;
    void (async () => {
      const stored = await getToken(AUTH_KEYS.accessToken);
      const expiryMs = getTokenExpiryMs(stored);
      if (!stored || expiryMs === null) return;
      if (expiryMs - Date.now() > RESUME_REFRESH_LEAD_MS) return;
      const res = await refreshAccessToken();
      if (res.success && res.accessToken) {
        setAuthFromTokens(res.accessToken, res.refreshToken ?? null);
      } else if (res.terminal) {
        await clearAuth();
      }
      // Transient failure: the next request's reactive 401 path will catch it.
    })();
  });

  useEffect(() => {
    let mounted = true as boolean;
    void (async () => {
      try {
        // Snapshot the "had a session before this launch" signal up front,
        // BEFORE any refresh/clear path can wipe storage. If we discover the
        // stored token is expired and the refresh later fails, clearAuth
        // empties token storage — but the user still had a session, so the
        // splash gate should land them on /login, not the marketing page.
        const [priorAccess, priorRefresh] = await Promise.all([
          getToken(AUTH_KEYS.accessToken),
          getToken(AUTH_KEYS.refreshToken),
        ]);
        if (mounted && (priorAccess || priorRefresh)) {
          setHadPriorSession(true);
        }

        await getOrCreateDeviceId();

        // Resolve the final access token (stored token or up-front JWT
        // refresh) WITHOUT writing it to in-memory auth yet. We need the
        // eventual userId first so we can point the itinerary store's
        // persist scope at the right slot before its first rehydrate —
        // otherwise the splash would briefly reveal the anonymous scope's
        // content before flipping to the user's.
        let finalToken: string | null = null;
        const stored = await getToken(AUTH_KEYS.accessToken);
        // If the stored access token is already expired, try a refresh
        // up-front — this avoids a guaranteed 401 on the first request
        // after a long-idle reopen.
        const expiryMs = getTokenExpiryMs(stored);
        if (stored && expiryMs !== null && expiryMs <= Date.now()) {
          const refreshed = await refreshAccessToken();
          if (refreshed.success && refreshed.accessToken) {
            finalToken = refreshed.accessToken;
          } else {
            // Refresh failed — clear so the user lands on sign-in.
            await clearAuth();
            finalToken = null;
          }
        } else {
          finalToken = stored;
        }

        if (!mounted) return;

        // Align the itinerary store's persist scope with the resolved
        // user BEFORE the first rehydrate runs. With `skipHydration: true`
        // in the persist config, the store stays at default-empty until
        // this call returns. SplashGate keeps the splash up until the
        // rehydrate resolves, so the user never sees the wrong scope.
        await initStorageScopeOnBoot(getTokenSub(finalToken));

        // Now wire in-memory auth — components that react to
        // isAuthenticated will see consistent (already-hydrated) trips.
        setAuthFromTokens(finalToken);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [setAuthFromTokens, clearAuth]);

  const setAuthenticated = useCallback((v: boolean) => {
    setIsAuthenticated(v);
  }, []);

  const setUserInfo = useCallback((info: UserInfo | null) => {
    setUser(info);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      loading,
      user,
      hadPriorSession,
      setAuthenticated,
      setAuthFromTokens,
      persistAndSetAuth,
      setUserInfo,
      logout,
    }),
    [isAuthenticated, loading, user, hadPriorSession, setAuthenticated, setAuthFromTokens, persistAndSetAuth, setUserInfo, logout]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { AUTH_KEYS };
